import React from 'react';
import {
    FaExclamationTriangle,
    FaTimes,
    FaBuilding,
    FaSpinner,
    FaCheckCircle,
    FaHistory,
} from 'react-icons/fa';
import { BLEEP_SERVER_NAMES } from '../../utils/constants';

const CrossDivisionScopeModal = ({
    isOpen,
    scope,
    kind = 'profile', // 'profile' | 'training'
    operation = 'approve', // 'approve' | 'revert'
    selectedPaths = [],
    dateChoices = {},
    assignments = [],
    onConfirm,
    onCancel,
    busy = false,
}) => {
    if (!isOpen || !scope) return null;

    const isRevert = operation === 'revert';
    const targets = Array.isArray(scope.affectedDivisions) ? scope.affectedDivisions : [];
    const plans = Array.isArray(scope.divisionPlans) ? scope.divisionPlans : [];
    const selectedSet = new Set(selectedPaths || []);

    const title = isRevert ? 'Restore changes across divisions?' : 'Approve across divisions?';
    const confirmLabel = isRevert ? 'Restore listed divisions' : 'Approve listed divisions';

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
        }}>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                maxWidth: '660px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #edf2f7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            padding: '10px',
                            borderRadius: '10px',
                            backgroundColor: isRevert ? '#FFF5F5' : '#E7F2EF',
                            color: isRevert ? '#C53030' : '#176B64',
                        }}>
                            {isRevert ? <FaHistory style={{ fontSize: '18px' }} /> : <FaCheckCircle style={{ fontSize: '18px' }} />}
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
                                {title}
                            </h3>
                            <div style={{ fontSize: '12px', color: '#718096' }}>
                                Cross-Division Intelligence Verification
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#a0aec0',
                            fontSize: '18px',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            padding: '4px',
                        }}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '24px', fontSize: '13px', color: '#4a5568', lineHeight: 1.6 }}>
                    <p style={{ margin: '0 0 16px 0', fontSize: '13.5px' }}>
                        {isRevert
                            ? 'This restores the saved changes in every division listed below. If any affected record has changed, the whole operation stops.'
                            : 'The selected profile or training changes will apply to every verified employee record listed below. Login and account status stay unchanged.'}
                    </p>

                    {/* Affected Divisions Box */}
                    <div style={{
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        padding: '14px 18px',
                        marginBottom: '18px',
                    }}>
                        <div style={{
                            fontSize: '11.5px',
                            fontWeight: '700',
                            color: '#718096',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '10px',
                        }}>
                            Verified Franchise Employee Targets ({targets.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {targets.map((t, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '8px 12px',
                                        backgroundColor: '#ffffff',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FaBuilding style={{ color: '#176B64', fontSize: '12px' }} />
                                        <strong style={{ color: '#2d3748' }}>
                                            {BLEEP_SERVER_NAMES[t.serverID] || t.serverID}
                                        </strong>
                                    </div>
                                    <div style={{ fontFamily: 'monospace', color: '#718096', fontSize: '12px' }}>
                                        {t.employeeDocumentID || 'Original approval'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Profile Changes Breakdown across divisions */}
                    {!isRevert && kind === 'profile' && plans.length > 0 && (
                        <div style={{ marginBottom: '18px' }}>
                            <div style={{
                                fontSize: '11.5px',
                                fontWeight: '700',
                                color: '#718096',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: '10px',
                            }}>
                                Target Field Changes Per Division
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {plans.map((entry, pIdx) => {
                                    const planChanges = [
                                        ...(entry.plan?.changes || []),
                                        ...(entry.plan?.unchanged || []),
                                    ].filter((row) => selectedSet.has(row.target));

                                    if (planChanges.length === 0) return null;

                                    return (
                                        <div key={pIdx} style={{
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            backgroundColor: '#ffffff',
                                        }}>
                                            <div style={{ fontWeight: '700', color: '#2b6cb0', marginBottom: '8px' }}>
                                                {BLEEP_SERVER_NAMES[entry.serverID] || entry.serverID}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                {planChanges.map((row, rIdx) => {
                                                    const approvedVal = dateChoices[row.target] ?? row.proposed;
                                                    return (
                                                        <div key={rIdx} style={{
                                                            padding: '6px 10px',
                                                            borderRadius: '4px',
                                                            backgroundColor: '#f8fafc',
                                                            fontSize: '12px',
                                                        }}>
                                                            <div style={{ fontWeight: '600', color: '#2d3748' }}>
                                                                {row.target}
                                                            </div>
                                                            <div style={{ color: '#718096', marginTop: '2px' }}>
                                                                Current: <code>{JSON.stringify(row.current)}</code> → Approved: <code style={{ color: '#176B64', fontWeight: '700' }}>{JSON.stringify(approvedVal)}</code>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Training Assignments Breakdown */}
                    {!isRevert && kind === 'training' && (
                        <div style={{ marginBottom: '18px' }}>
                            <div style={{
                                fontSize: '11.5px',
                                fontWeight: '700',
                                color: '#718096',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: '10px',
                            }}>
                                Selected Training Assignments ({assignments.length})
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {assignments.map((item, aIdx) => (
                                    <div key={aIdx} style={{
                                        padding: '8px 12px',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: '12px',
                                    }}>
                                        <strong style={{ color: '#2d3748' }}>{item.courseKey}</strong>
                                        <span style={{ color: '#718096' }}>Expires: <strong>{item.expiryDate || 'No expiry'}</strong></span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ fontSize: '12px', color: '#718096', marginTop: '8px' }}>
                                Missing records will be added. Identical evidence is kept. Conflicting evidence stops the approval for all divisions.
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Buttons */}
                <div style={{
                    padding: '16px 24px',
                    borderTop: '1px solid #edf2f7',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    backgroundColor: '#f8fafc',
                    borderBottomLeftRadius: '16px',
                    borderBottomRightRadius: '16px',
                }}>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        style={{
                            backgroundColor: '#ffffff',
                            color: '#4a5568',
                            border: '1px solid #cbd5e0',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: busy ? 'not-allowed' : 'pointer',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm(scope.groupID)}
                        disabled={busy}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: isRevert ? '#C53030' : '#176B64',
                            color: '#ffffff',
                            border: 'none',
                            padding: '8px 20px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '700',
                            cursor: busy ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {busy && <FaSpinner className="fa-spin" />}
                        <span>{confirmLabel}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CrossDivisionScopeModal;
